import React from 'react';
import { motion } from 'framer-motion';
import { CountdownTimer } from '../CountdownTimer';
import { CalendarIcon, ClockIcon, AwardIcon, UsersIcon } from 'lucide-react';
interface HeroSectionProps {
  targetDate: Date;
  checkoutUrl: string;
}
export const HeroSection = ({
  targetDate,
  checkoutUrl
}: HeroSectionProps) => {
  const title = 'DOMINA TU CRÉDITO EN USA';
  const letters = title.split('');
  const totalLetters = letters.length;
  return <section className="relative w-full bg-white" data-id="element-160">
      {/* Full-width Hero Image */}
      <div className="w-full" data-id="element-161">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iN2XvZvLra57jsSXdwUH7b/EDIO_%281%29.png" alt="Hero" className="w-full h-auto object-cover" data-id="element-162" />
      </div>
      {/* Content Section - White Background */}
      <div className="bg-white py-12 sm:py-16 px-4" data-id="element-163">
        <div className="container mx-auto max-w-5xl" data-id="element-164">
          {/* Curved Title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center mb-6" data-id="element-165">
            <div className="flex justify-center items-center mb-4" data-id="element-166">
              <h1 className="flex justify-center items-end max-w-xl sm:max-w-2xl" style={{
              height: '45px'
            }} data-id="element-167">
                {letters.map((letter, index) => {
                const midPoint = totalLetters / 2;
                const distance = index - midPoint;
                const rotation = distance * 2;
                const translateY = Math.abs(distance) * -1.5;
                return <motion.span key={index} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.3,
                  delay: index * 0.02
                }} className="inline-block text-lg sm:text-xl md:text-2xl font-bold text-black" style={{
                  transform: `rotate(${rotation}deg) translateY(${translateY}px)`,
                  transformOrigin: 'center bottom',
                  marginLeft: letter === ' ' ? '0.3rem' : '0.02rem',
                  marginRight: letter === ' ' ? '0.3rem' : '0.02rem',
                  letterSpacing: '-0.02em'
                }} data-id="element-168">
                      {letter === ' ' ? '\u00A0' : letter}
                    </motion.span>;
              })}
              </h1>
            </div>
            {/* Subtitle */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-xl sm:text-2xl md:text-3xl text-gray-800 font-normal" data-id="element-169">
              Masterclass
            </motion.p>
          </motion.div>
          {/* Info Cards Row */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center max-w-3xl mx-auto mb-8" data-id="element-170">
            {/* Left Card - Date & Time */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-sm w-full sm:w-auto" data-id="element-171">
              <div className="flex items-center gap-3 text-gray-800 mb-2" data-id="element-172">
                <CalendarIcon size={22} className="text-[#7B2BFD]" data-id="element-173" />
                <span className="text-base font-semibold" data-id="element-174">
                  7 de Diciembre 2025
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-800" data-id="element-175">
                <ClockIcon size={22} className="text-[#7B2BFD]" data-id="element-176" />
                <span className="text-base font-semibold" data-id="element-177">
                  8 horas vía Zoom
                </span>
              </div>
            </motion.div>
            {/* Right Card - Mentorship */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-sm w-full sm:w-auto" data-id="element-178">
              <div className="flex items-center gap-3 text-gray-800 mb-2" data-id="element-179">
                <AwardIcon size={22} className="text-[#7B2BFD]" data-id="element-180" />
                <span className="text-base font-semibold" data-id="element-181">
                  Mentoría Privada
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-800" data-id="element-182">
                <UsersIcon size={22} className="text-[#7B2BFD]" data-id="element-183" />
                <span className="text-base font-semibold" data-id="element-184">
                  Capacidad Limitada
                </span>
              </div>
            </motion.div>
          </div>
          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex justify-center mb-6" data-id="element-185">
            <motion.a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="inline-block px-10 sm:px-14 py-4 bg-white text-[#7B2BFD] text-lg sm:text-xl font-bold rounded-full transition-all duration-300" style={{
            border: '3px solid #7B2BFD',
            boxShadow: '0 0 20px rgba(123, 43, 253, 0.4), 0 0 40px rgba(123, 43, 253, 0.2)'
          }} animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} whileHover={{
            scale: 1.08,
            boxShadow: '0 0 30px rgba(123, 43, 253, 0.6), 0 0 60px rgba(123, 43, 253, 0.4)'
          }} whileTap={{
            scale: 0.95
          }} data-id="element-186">
              RESERVAR MI CUPO
            </motion.a>
          </motion.div>
          {/* Countdown Timer */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="flex justify-center" data-id="element-187">
            <CountdownTimer targetDate={targetDate} data-id="element-188" />
          </motion.div>
        </div>
      </div>
    </section>;
};